import { useRequestURL, useRouter } from '#imports'
import { type Ref, computed } from 'vue'

import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { getAllMarkets } from '@backmarket/nuxt-module-marketplace/getAllMarkets'
import { toNuxtLink } from '@backmarket/nuxt-module-navigation/toNuxtLink'
import { toBcp47Locale } from '@backmarket/utils/string/toBcp47Locale'

import { useMasterPP } from '~/scopes/product-core/composables/useMasterPP/useMasterPP'
import { PRODUCT } from '~/scopes/product-core/route-names'

export function useProductHeadLink({
  productResponse,
}: {
  productResponse: Ref<GetProductResponse | undefined>
}) {
  const i18n = useI18n()
  const router = useRouter()
  const { protocol, hostname } = useRequestURL()
  const { isMasterPPUrl } = useMasterPP()

  const markets = getAllMarkets()

  /**
   * Alternates links are retrieved from the product endpoint, helping google to crawl the current product in alternative locales.
   * Canonical is here to help preventing duplicate content with URLs containing query params for instance.
   */
  const link = computed(() => {
    if (!productResponse.value) {
      return []
    }

    const links = []

    if (productResponse.value.parentProductSlug) {
      links.push({
        href: `${protocol}//${hostname}${router.resolve(toNuxtLink({ name: PRODUCT.MASTER_PP, params: { locale: toBcp47Locale(i18n.currentLocale), slug: productResponse.value.parentProductSlug } })).href}`,
        rel: 'canonical',
      })
    } else {
      links.push({
        href: `${protocol}//${hostname}${router.resolve(toNuxtLink({ name: PRODUCT.HOME, params: { locale: toBcp47Locale(i18n.currentLocale), slugV2: productResponse.value.seo.slug, uuid: productResponse.value.productId } })).href}`,
        rel: 'canonical',
      })
    }

    links.push(
      ...Object.values(productResponse.value.seo.marketsLinks).map(
        ({ countryCode, slug }) => {
          const locale = toBcp47Locale(
            markets[countryCode]?.defaultLocale ?? i18n.currentLocale,
          )

          if (isMasterPPUrl.value) {
            return {
              rel: 'alternate',
              hreflang: locale,
              href: `${protocol}//www${markets[countryCode]?.domain}${router.resolve(toNuxtLink({ name: PRODUCT.MASTER_PP, params: { locale, slug: productResponse.value?.parentProductSlug } })).href}`,
            }
          }

          return {
            rel: 'alternate',
            hreflang: locale,
            href: `${protocol}//www${markets[countryCode]?.domain}${router.resolve(toNuxtLink({ name: PRODUCT.HOME, params: { locale, slugV2: slug, uuid: productResponse.value?.productId } })).href}`,
          }
        },
      ),
    )

    return links
  })

  return { link }
}
